import { useState } from "react";
import { CheckCircle, WarningCircle, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/ui/Button";
import { ButtonTypes, Variants } from "@/ui/types";
import { NiceBadge } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/PromptMessageContainer";
import { ArtifactType } from "@/ui/CommunicationView/ChatConversation/types";
import { STEP_ARTIFACT, APPROVAL_ARTIFACT } from "./claudeStream";
import { approveCopilot } from "./useClaudeStream";
import "./copilot.css";

// CoPilot, Claude side (CP-3/CP-4): the status steps and Approve cards, registered in the chat's
// existing artifact registry — the same way the page builder's cards are — so the panel itself
// doesn't change. Built from the panel's own parts (NiceBadge, Button).

export const StepCard = ({ payload }: { payload: any }) => {
  const status = payload?.status || "running";
  return (
    <NiceBadge
      // NiceBadge Title-Cases anything with "-" or "_"; non-breaking hyphens keep the label as written
      caption={String(payload?.label || "Working…").replace(/-/g, "\u2011").replace(/_/g, " ")}
      isLoading={status === "running"}
      icon={status === "failed" ? <WarningCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
    />
  );
};

export const ApprovalCard = ({ payload }: { payload: any }) => {
  const [sending, setSending] = useState<null | "approve" | "cancel">(null);
  const [failed, setFailed] = useState(false);
  const status = payload?.status || "waiting";
  const answer = async (approve: boolean) => {
    setSending(approve ? "approve" : "cancel");
    setFailed(false);
    const ok = await approveCopilot(String(payload?.id || ""), approve);
    if (!ok) {
      setFailed(true);
      setSending(null);
    }
  };
  return (
    <div className="copilot-approval" data-status={status} data-testid="copilot-approval">
      <div className="copilot-approval-head">
        <ShieldCheck className="w-4 h-4" />
        {status === "waiting" ? "Needs your OK · changes something live" : status === "approved" ? "Approved" : "Cancelled · nothing changed"}
      </div>
      <p className="copilot-approval-text">{String(payload?.summary || "")}</p>
      {status === "waiting" && (
        <div className="copilot-approval-actions">
          <Button label={sending === "approve" ? "Approving…" : "Approve"} variant={Variants.PRIMARY} onClick={() => !sending && answer(true)} />
          <Button label={sending === "cancel" ? "Cancelling…" : "Cancel"} type={ButtonTypes.OUTLINED} onClick={() => !sending && answer(false)} />
        </div>
      )}
      {failed && <p className="copilot-approval-error">That didn't go through. The card may have expired; ask again.</p>}
    </div>
  );
};

export const copilotArtifacts: ArtifactType[] = [
  { id: STEP_ARTIFACT, type: "tool_call", component: StepCard },
  { id: STEP_ARTIFACT, hideMessage: true }, // the raw tool result: the step card already says it's done
  { id: APPROVAL_ARTIFACT, component: ApprovalCard },
] as ArtifactType[];
